import React, { useEffect } from 'react';
import { useMotion } from '../../hooks/useMotion.js';
import { WheelCarousel } from '../../components/WheelCarousel/WheelCarousel.jsx';
import './Approach.css';

const investmentPrinciples = [
  {
    num: '01',
    title: 'Keep It Simple',
    description:
      'If an investment cannot be explained plainly, it is not understood well enough to be owned. Complexity should be justified by what it adds, not assumed.',
    image: '/media/modern-architecture-and-cityscape-in-an-urban-envi-2026-09-15-20-02-22-utc.webp',
  },
  {
    num: '02',
    title: 'Be Disciplined',
    description:
      'A strategy is only as good as the consistency with which it is followed. We hold to the agreed process through market noise rather than reacting to it.',
    image: '/media/low-angle-view-modern-buildings-against-sky.webp',
  },
  {
    num: '03',
    title: 'Preserve Wealth',
    description:
      'Protecting capital comes before growing it. Avoiding permanent loss does more for long-term returns than chasing the best quarter.',
    image: '/media/financial-growth-investment-success-with-stacks-gold-coins.webp',
  },
  {
    num: '04',
    title: 'Diversify',
    description:
      'Spreading exposure across assets, regions and strategies reduces the damage any single outcome can do to the portfolio.',
    image: '/media/double-exposure-digital-graphs-with-stacked-coins.webp',
  },
  {
    num: '05',
    title: 'Manage Your Risks',
    description:
      'Risk is measured and sized deliberately against your profile, then reviewed as circumstances and markets change.',
    image: '/media/sculpture-city-long-exposure-complex-amazing.webp',
  },
];

export const Approach = () => {
  useMotion();

  useEffect(() => {
    document.title = 'Investment Approach | ASTRID FIRST Wealth Advisory';
  }, []);

  return (
    <div className="approach-page">
      <header className="page-head">
        <div className="page-head-inner">
          <p className="crumb">
            <a href="/">Home</a> / Investment Approach
          </p>
          <h1 data-reveal="chars">Investment Approach</h1>
          <p className="lead" data-reveal="fade">
            Five principles, and the process that puts them to work.
          </p>
        </div>
      </header>

      {/* Full-width Pinned Investment Principles Wheel Section */}
      <WheelCarousel
        items={investmentPrinciples}
        eyebrow="What governs our strategies"
        title="Investment Principles"
        lead="Here at ASTRID FIRST, our strategies are governed by five fundamental principles."
        pinned={true}
      />

      <div className="spine">

        <section className="section shell">
          <div className="track">
            <div className="track-head track-head--sticky">
              <p className="eyebrow">In practice</p>
              <h2 data-reveal="lines">
                Key Investment Principles for Smart Financial Decisions
              </h2>
            </div>
            <div className="prose">
              <p className="lead" data-reveal="fade">
                Investment principles guide informed financial decisions.
              </p>
              <p data-reveal="fade">
                The key ones are diversification, risk management, a long-term
                focus, asset allocation, regular review, cost efficiency and
                informed decision-making. Together they help manage risk,
                maximise returns and reach financial goals through a balanced
                and strategic approach.
              </p>
            </div>
          </div>
        </section>

        <section className="section shell">
          <div className="track">
            <div className="track-head">
              <p className="eyebrow">Five steps</p>
              <h2 data-reveal="lines">Our Approach</h2>
            </div>
            <div>
              <p
                className="lead"
                data-reveal="fade"
                style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)' }}
              >
                ASTRID FIRST's five-step investment process is built from decades
                of investment experience and gives our clients optimal portfolio
                performance relative to their risk and return objectives.
              </p>

              <div className="steps" data-reveal="group">
                <div className="step">
                  <span className="step-num">Step 01</span>
                  <h4>Understand</h4>
                  <p>Based on your priorities, needs, and goals.</p>
                </div>
                <div className="step">
                  <span className="step-num">Step 02</span>
                  <h4>Propose &amp; Implement</h4>
                  <p>Bespoke solutions for you.</p>
                </div>
                <div className="step">
                  <span className="step-num">Step 03</span>
                  <h4>Monitor &amp; Review</h4>
                  <p>Periodic review of the portfolio.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="shell">
            <div className="frame" data-video-reveal data-scale>
              <div
                className="slot slot--wide"
                data-slot="Singapore skyline at twilight — 16:9"
              >
                <img
                  src="/media/singapore-downtown-skyline-bay-area-twilight.webp"
                  alt="Singapore Downtown Skyline and Bay Area at Twilight"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="section shell">
          <div className="track">
            <div className="track-head track-head--sticky">
              <p className="eyebrow">The whole picture</p>
              <h2 data-reveal="lines">
                Comprehensive Investment Approach for Optimal Financial Growth
              </h2>
            </div>
            <div className="prose">
              <p className="lead" data-reveal="fade">
                Our approach aims for growth through strategic asset allocation,
                thorough risk management and a long-term perspective.
              </p>
              <p data-reveal="fade">
                We diversify investments, review and rebalance portfolios
                regularly, and prioritise cost efficiency and informed
                decision-making to maximise returns while minimising risk.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};